import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Table,
  Tag,
  Space,
  Popconfirm,
} from 'antd';
import { PlusOutlined, KeyOutlined, DeleteOutlined } from '@ant-design/icons';
import { createUploader, getCampusAdminList, campusResetPassword, campusDeleteAdmin } from '../../services/admin';
import { AdminAccount, PaginatedResponse } from '../../types';

const { Option } = Select;

const CampusAccountManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponse<AdminAccount> | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [campusInfo, setCampusInfo] = useState<any>(null);
  const [resetPwdModal, setResetPwdModal] = useState<{ visible: boolean; account: string; newPassword: string }>({ visible: false, account: '', newPassword: '' });

  useEffect(() => {
    const storedCampusInfo = localStorage.getItem('campusInfo');
    if (storedCampusInfo) {
      const parsed = JSON.parse(storedCampusInfo);
      setCampusInfo(parsed);
      form.setFieldsValue({ campusId: parsed.id });
    }
  }, [form]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 使用校区管理员专用的 API
      const res = await getCampusAdminList({ pageSize: 100 });
      setData(res);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      const res = await createUploader(values.account, values.campusId);
      Modal.success({
        title: '上传专员账号创建成功',
        content: (
          <div>
            <p>账号：{res.account}</p>
            <p>初始密码：{res.initialPassword}</p>
            <p style={{ color: '#ff4d4f' }}>请保存好初始密码！</p>
            <p style={{ color: '#888', fontSize: 12 }}>上传专员权限：可上传学生信息和成绩信息，但不能审核成绩。</p>
          </div>
        ),
      });
      setModalVisible(false);
      form.resetFields();
      if (campusInfo) {
        form.setFieldsValue({ campusId: campusInfo.id });
      }
      fetchData();
    } catch (error) {
      message.error('创建失败');
    }
  };

  const handleResetPassword = async (id: string, account: string) => {
    try {
      const res = await campusResetPassword(id);
      setResetPwdModal({ visible: true, account, newPassword: res.newPassword });
    } catch {
      message.error('密码重置失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await campusDeleteAdmin(id);
      message.success('账号删除成功');
      fetchData();
    } catch {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: '账号',
      dataIndex: 'account',
      key: 'account',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color="green">上传专员</Tag>
      ),
    },
    {
      title: '所属校区',
      dataIndex: 'campusName',
      key: 'campusName',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'enabled' ? 'success' : 'default'}>
          {status === 'enabled' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: AdminAccount) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => handleResetPassword(record.id, record.account)}
          >
            重置密码
          </Button>
          <Popconfirm
            title={`确定要删除上传专员「${record.account}」吗？`}
            description="删除后不可恢复，如该账号有成绩记录则无法删除。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="上传专员账号管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
            创建上传专员
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={data?.list}
          loading={loading}
          rowKey="id"
          pagination={false}
        />
      </Card>

      <Modal
        title="创建上传专员账号"
        open={modalVisible}
        forceRender
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="account"
            label="账号"
            rules={[
              { required: true, message: '请输入账号' },
              { min: 3, message: '账号至少3位' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: '账号只能包含字母、数字、下划线和横线' },
            ]}
          >
            <Input placeholder="请输入账号" />
          </Form.Item>

          <Form.Item
            name="campusId"
            label="所属校区"
            rules={[{ required: true, message: '请选择所属校区' }]}
            extra="上传专员将属于您所在的校区"
          >
            <Select placeholder="请选择所属校区">
              {campusInfo && (
                <Option value={campusInfo.id}>
                  {campusInfo.campusName}
                </Option>
              )}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 密码重置结果弹窗 */}
      <Modal
        title="密码重置成功"
        open={resetPwdModal.visible}
        onCancel={() => setResetPwdModal({ ...resetPwdModal, visible: false })}
        footer={
          <Button type="primary" onClick={() => setResetPwdModal({ ...resetPwdModal, visible: false })}>
            确定
          </Button>
        }
        width={420}
      >
        <div style={{ padding: '12px 0' }}>
          <p>账号 <strong>{resetPwdModal.account}</strong> 的密码已重置为：</p>
          <div style={{
            background: '#f5f5f5',
            padding: '12px 16px',
            borderRadius: 8,
            fontSize: 18,
            fontWeight: 600,
            textAlign: 'center',
            letterSpacing: 2,
            margin: '16px 0',
            fontFamily: 'monospace',
          }}>
            {resetPwdModal.newPassword}
          </div>
          <p style={{ color: '#ff4d4f', fontSize: 13 }}>
            请立即将新密码告知该上传专员，并提醒其首次登录修改密码。
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default CampusAccountManagement;
